import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Download, Share, Plus, X } from "lucide-react";
import { pwaConfig } from "@/lib/pwa-config";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>; // i18n-ignore
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>; // i18n-ignore
}

const STORAGE_KEY = "kitdex.installPrompt.v1";
const ENGAGEMENT_KEY = "kitdex.pageViews.v1";
const MIN_PAGE_VIEWS = pwaConfig.installPromptMinPageViews;

type StoredState = {
  dismissedAt?: number;
  installedAt?: number;
  shownAt?: number;
  nativePromptSeen?: boolean;
  iosTutorialSeen?: boolean;
};

function readState(): StoredState {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}");
  } catch {
    return {};
  }
}

function writeState(s: StoredState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* ignore */
  }
}

function readPageViews(): number {
  try {
    return Number(localStorage.getItem(ENGAGEMENT_KEY) ?? "0");
  } catch {
    return 0;
  }
}

function writePageViews(n: number) {
  try {
    localStorage.setItem(ENGAGEMENT_KEY, String(n));
  } catch {
    /* ignore */
  }
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const mq = window.matchMedia?.("(display-mode: standalone)").matches;
  const ios = (window.navigator as Navigator & { standalone?: boolean }).standalone;
  return Boolean(mq || ios);
}

function isIos(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const isAppleDevice = /iPad|iPhone|iPod/.test(ua) ||
    (ua.includes("Mac") && "ontouchend" in document);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return isAppleDevice && isSafari;
}

export function InstallPrompt() {
  const [location] = useLocation();
  const [pageViews, setPageViews] = useState<number>(() => readPageViews());
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [variant, setVariant] = useState<"native" | "ios">("native");

  // Track engagement per route change (real page views, not app loads).
  useEffect(() => {
    if (isStandalone()) return;
    const next = readPageViews() + 1;
    writePageViews(next);
    setPageViews(next);
  }, [location]);

  // Listen for the native install event once.
  useEffect(() => {
    if (isStandalone()) return;
    const stored = readState();
    if (stored.installedAt) return;

    function onBeforeInstall(e: Event) {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      setVariant("native");
    }
    function onInstalled() {
      writeState({ ...readState(), installedAt: Date.now() });
      setVisible(false);
      setDeferred(null);
    }
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Decide whether to show the prompt based on engagement.
  useEffect(() => {
    if (visible) return;
    if (isStandalone()) return;
    if (pageViews < MIN_PAGE_VIEWS) return;

    const stored = readState();
    if (stored.installedAt) return;

    if (deferred) {
      // Native: truly one-time. After the first show, never re-surface.
      if (stored.nativePromptSeen) return;
      setVariant("native");
      setVisible(true);
      writeState({ ...stored, nativePromptSeen: true, shownAt: Date.now() });
      return;
    }

    if (isIos() && !stored.iosTutorialSeen) {
      // iOS: truly one-time tutorial (no re-show after dismiss).
      setVariant("ios");
      setVisible(true);
      writeState({ ...stored, shownAt: Date.now() });
    }
  }, [pageViews, deferred, visible]);

  function dismiss() {
    const stored = readState();
    if (variant === "ios") {
      writeState({ ...stored, iosTutorialSeen: true, dismissedAt: Date.now() });
    } else {
      writeState({ ...stored, nativePromptSeen: true, dismissedAt: Date.now() });
    }
    setVisible(false);
  }

  async function install() {
    if (!deferred) return;
    try {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice.outcome === "accepted") {
        writeState({ ...readState(), installedAt: Date.now() });
      } else {
        writeState({ ...readState(), nativePromptSeen: true, dismissedAt: Date.now() });
      }
    } catch {
      writeState({ ...readState(), nativePromptSeen: true, dismissedAt: Date.now() });
    } finally {
      setDeferred(null);
      setVisible(false);
    }
  }

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="install-prompt-title"
      className="fixed inset-x-3 bottom-3 z-[60] sm:left-auto sm:right-4 sm:bottom-4 sm:max-w-sm"
    >
      <div className="border-2 border-foreground bg-card shadow-[6px_6px_0_0_hsl(var(--foreground))]">
        <div className="flex items-start justify-between gap-2 px-4 pt-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Install Kitdex
          </span>
          <button
            onClick={dismiss}
            aria-label="Dismiss install prompt" /* i18n-ignore */
            className="-mr-1 -mt-0.5 p-1 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-4 pb-4 pt-1">
          <h3 id="install-prompt-title" className="font-serif text-xl font-black leading-tight">
            Add Kitdex to your home screen.
          </h3>
          {variant === "native" ? (
            <>
              <p className="mt-2 text-sm text-muted-foreground">
                One tap to launch, works on slow connections, opens straight to the archive.
              </p>
              <div className="mt-4 flex gap-2">
                <button
                  onClick={install}
                  className="inline-flex items-center gap-2 bg-foreground text-background px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[hsl(var(--primary))] transition-colors"
                >
                  <Download className="h-4 w-4" /> Install
                </button>
                <button
                  onClick={dismiss}
                  className="px-3 py-2 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground"
                >
                  Not now
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="mt-2 text-sm text-muted-foreground">
                Tap{" "}
                <Share className="inline h-4 w-4 align-text-bottom" />{" "}
                in Safari, then{" "}
                <span className="inline-flex items-center gap-1 font-medium text-foreground">
                  <Plus className="h-3.5 w-3.5" /> Add to Home Screen
                </span>
                .
              </p>
              <div className="mt-4 flex gap-2">
                <button
                  onClick={dismiss}
                  className="bg-foreground text-background px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[hsl(var(--primary))] transition-colors"
                >
                  Got it
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
